import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { webApi } from '../services/api';

const SUBTITLE = 'Update your password to keep your account secure';

function LockIcon() {
  return (
    <svg className="change-pwd-field-icon" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"
      />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78 3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
      />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
      />
    </svg>
  );
}

function PasswordField({ id, label, value, onChange, show, onToggleShow, autoComplete }) {
  return (
    <div className="change-pwd-field-wrap">
      <label htmlFor={id} className="change-pwd-sr-only">
        {label}
      </label>
      <span className="change-pwd-field-leading" aria-hidden="true">
        <LockIcon />
      </span>
      <input
        id={id}
        className="change-pwd-input"
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={label}
        autoComplete={autoComplete}
      />
      <button
        type="button"
        className="change-pwd-toggle"
        onClick={onToggleShow}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? <EyeIcon /> : <EyeOffIcon />}
      </button>
    </div>
  );
}

function ChangePasswordPage() {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState({ kind: '', text: '' });

  const clientError = () => {
    if (newPassword.length < 6) return 'New password must be at least 6 characters long';
    if (newPassword === currentPassword) return 'New password must be different from your current password';
    if (newPassword !== confirmPassword) return 'New password and confirmation do not match';
    return '';
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = clientError();
    if (err) {
      setBanner({ kind: 'error', text: err });
      return;
    }
    setSubmitting(true);
    setBanner({ kind: '', text: '' });
    try {
      await webApi.changePassword({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setBanner({ kind: 'success', text: 'Password changed successfully' });
    } catch (err) {
      const msg = err.message || 'Failed to change password';
      setBanner({
        kind: 'error',
        text: msg.replace(/^Failed to change password:\s*/i, '')
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="change-pwd-page">
      <header className="change-pwd-topbar">
        <button type="button" className="change-pwd-back" onClick={() => navigate(-1)} aria-label="Go back">
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </button>
        <h1 className="change-pwd-topbar-title">Change Password</h1>
        <span className="change-pwd-topbar-spacer" aria-hidden="true" />
      </header>

      <div className="change-pwd-body">
        <p className="change-pwd-subtitle">{SUBTITLE}</p>

        {banner.text ? (
          <p className={`change-pwd-banner change-pwd-banner--${banner.kind}`} role="status">
            {banner.text}
          </p>
        ) : null}

        <form className="change-pwd-form" onSubmit={onSubmit} noValidate>
          <PasswordField
            id="change-pwd-current"
            label="Current Password"
            value={currentPassword}
            onChange={setCurrentPassword}
            show={showCurrent}
            onToggleShow={() => setShowCurrent((s) => !s)}
            autoComplete="current-password"
          />
          <PasswordField
            id="change-pwd-new"
            label="New Password"
            value={newPassword}
            onChange={setNewPassword}
            show={showNew}
            onToggleShow={() => setShowNew((s) => !s)}
            autoComplete="new-password"
          />
          <PasswordField
            id="change-pwd-confirm"
            label="Confirm New Password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            show={showConfirm}
            onToggleShow={() => setShowConfirm((s) => !s)}
            autoComplete="new-password"
          />

          <section className="change-pwd-requirements" aria-label="Password requirements">
            <h2 className="change-pwd-requirements-title">Password Requirements:</h2>
            <ul className="change-pwd-requirements-list">
              <li>At least 6 characters long</li>
              <li>Different from your current password</li>
            </ul>
          </section>

          <button type="submit" className="change-pwd-submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ChangePasswordPage;
